import checkIns from '../data/sampleData';
import {
  ADMIN_API_BASE_URL,
  BUSINESS4FLYO_ADMIN_API_BASE_URL,
  FLYO4BUSINESS_ADMIN_API_BASE_URL,
  INTERNAL_API_V1_BASE_URL,
  INTERNAL_API_V2_BASE_URL,
  TRIP_API_BASE_URL,
} from '../config/api';

// Simulated API delay
const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));
const API_BASE_URL = ADMIN_API_BASE_URL;
const adminUrl = (path) => `${ADMIN_API_BASE_URL}${path}`;
const business4FlyoAdminUrl = (path) => `${BUSINESS4FLYO_ADMIN_API_BASE_URL}${path}`;
const flyo4BusinessAdminUrl = (path) => `${FLYO4BUSINESS_ADMIN_API_BASE_URL}${path}`;
const internalApiV1Url = (path) => `${INTERNAL_API_V1_BASE_URL}${path}`;
const internalApiV2Url = (path) => `${INTERNAL_API_V2_BASE_URL}${path}`;
const tripUrl = (path = '') => `${TRIP_API_BASE_URL}${path}`;

const normalizeExtRawStatus = (status) =>
  (status || '').toString().trim().toUpperCase().replace(/-/g, '_');

// FAILED and FAILED_BY_ADMIN stay apart: each has its own status card.
const normalizeExtCardStatus = (status) => {
  const normalized = normalizeExtRawStatus(status).toLowerCase();
  if (normalized === 'in_progress') return 'in-progress';
  return normalized;
};

const mapExtCheckInDetail = (detail = {}, fallbackSubscriptionId = '') => {
  const rawStatus = normalizeExtRawStatus(detail.checkinStatus || detail.status);
  const firstFlight = Array.isArray(detail.flights) ? detail.flights[0] : null;
  const origin = detail.origin || firstFlight?.origin || '';
  const destination = detail.destination || firstFlight?.destination || '';

  return {
    ...detail,
    subscriptionId: detail.subscriptionId || detail.checkinId || fallbackSubscriptionId,
    checkinStatus: rawStatus,
    rawCheckinStatus: rawStatus,
    status: normalizeExtCardStatus(rawStatus),
    checkinSubStatus: detail.checkinSubStatus || detail.subStatus || '',
    statusMessage: detail.statusMessage || detail.checkInMessage || '',
    boardingPassUrls: detail.boardingPassUrls || detail.boarding_passes || [],
    airline: detail.airline || firstFlight?.airline || '',
    flightNumber: detail.flightNumber || firstFlight?.flightNumber || firstFlight?.flight_number || '',
    origin,
    destination,
    sector: detail.sector || (origin && destination ? `${origin}-${destination}` : ''),
    partnerVisibleStatus: detail.partnerVisibleStatus || '',
    partnerVisibleSubStatus: detail.partnerVisibleSubStatus || '',
    partnerVisibleMessage: detail.partnerVisibleMessage || ''
  };
};

// Get auth token from localStorage or use a fallback for development
const getAuthToken = () => {
  const token = localStorage.getItem('authToken');
  if (!token) {
    console.warn('No auth token found in localStorage, using fallback token for development');
    // Fallback token for development purposes
    const fallbackToken = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJ1c2VySWQiOjQxNiwiaWF0IjoxNzQxNDMzMDA3fQ.jwuU6IZSuk2mOjBSkianU8UE6TL5aCNgp-sbS1fBq94';
    // Store the fallback token in localStorage for consistency
    localStorage.setItem('authToken', fallbackToken);
    return fallbackToken;
  }
  return token;
};

// Check if token is expired and refresh if needed
const checkAndRefreshToken = () => {
  const loginTimestamp = localStorage.getItem('loginTimestamp');

  if (!loginTimestamp) {
    // If no timestamp exists, set one now
    localStorage.setItem('loginTimestamp', new Date().getTime().toString());
    return getAuthToken();
  }

  // Check if the session has expired (1 hour = 3600000 milliseconds)
  const currentTime = new Date().getTime();
  const sessionTime = parseInt(loginTimestamp, 10);
  const sessionDuration = currentTime - sessionTime;

  if (sessionDuration > 3600000) {
    // Session expired, clear auth data and use fallback
    console.warn('Token expired, refreshing with fallback token');
    localStorage.removeItem('authToken');
    localStorage.removeItem('userData');
    localStorage.removeItem('loginTimestamp');
    return getAuthToken();
  }

  return getAuthToken();
};

/**
 * Fetch external partner check-ins from the API
 * @param {Object} params - Query parameters
 * @param {number} params.page - Page number (0-based)
 * @param {string} params.pnr - Filter by PNR
 * @param {string} params.partnerName - Filter by partner name
 * @param {string} params.airlineName - Filter by airline name
 * @param {string} params.subscriptionId - Filter by subscription ID
 * @param {string} params.flightNumber - Filter by flight number
 * @param {string} params.checkinStatus - Filter by check-in status
 * @param {string} params.departureDateMin - Minimum departure date (YYYY-MM-DD)
 * @param {string} params.departureDateMax - Maximum departure date (YYYY-MM-DD)
 * @returns {Promise<Object>} Promise resolving to external partner check-ins data with pagination
 */
export const fetchExtPartnerCheckIns = async (params = { page: 0 }) => {
  try {
    // Build query parameters, filtering out empty values
    const queryParams = new URLSearchParams();

    // Always add partnerName=EXT_PARTNER for external partner data
    queryParams.append('partnerType', 'EXT_PARTNER');

    Object.entries(params).forEach(([key, value]) => {
      if (value !== null && value !== undefined && value !== '') {
        queryParams.append(key, value);
      }
    });

    const response = await fetch(adminUrl(`/getCheckinAttempts?${queryParams.toString()}`), {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${getAuthToken()}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      // Handle 401 errors specifically
      if (response.status === 401) {
        console.error('Authentication error: Token may be invalid or expired');
        throw new Error('Authentication error: Please refresh or log in again');
      }
      throw new Error(`API error: ${response.status}`);
    }

    const data = await response.json();

    if (!data.success) {
      throw new Error(data.message || 'Failed to fetch external partner check-ins');
    }

    // Helper function to transform API data to match the expected format
    const transformData = (items) => items.map(item => {
      const rawStatus = normalizeExtRawStatus(item.checkinStatus);

      return {
        subscriptionId: item.subscriptionId || 'N/A',
        partnerName: item.partnerName || 'N/A',
        airline: item.airline || 'N/A',
        flightNumber: item.flightNumber || 'N/A',
        sector: item.sector || 'N/A',
        departureDate: item.departureDate || '',
        pnr: item.pnr || 'N/A',
        checkinStatus: rawStatus,
        rawCheckinStatus: rawStatus,
        status: normalizeExtCardStatus(rawStatus),
        checkinSubStatus: item.checkinSubStatus || '',
        statusMessage: item.statusMessage || '',
        checkInStatus: rawStatus,
        checkInSubStatus: item.checkinSubStatus || '',
        checkInMessage: item.statusMessage || '',
        boardingPassUrls: item.boardingPassUrls || [],
        boardingPassRatio: item.boardingPassRatio || '0/0'
      };
    });

    // Transform main data array
    const transformedData = transformData(data.data);

    // Return data with pagination info
    return {
      data: transformedData,
      pagination: data.pagination
    };
  } catch (error) {
    console.error('Error fetching external partner check-ins:', error);
    throw error;
  }
};

/**
 * Get hardcoded JWT token for Business4Flyo API
 * @returns {string} Hardcoded JWT token
 */
const getBusiness4FlyoToken = () => {
  return 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJhZ2VudElkIjoyLCJhZ2VudFRlYW1NZW1iZXJJZCI6MSwidHlwZSI6ImFnZW50X3RlYW1fbWVtYmVyIiwiaWF0IjoxNzY3ODk5MjkyLCJleHAiOjE3Njg1MDQwOTJ9.PqfP15-xAl2T_TY319XY-r_e_28hFFuee-eRWHcc0i8';
};

/**
 * Fetch Business4Flyo web check-ins from the API
 * @param {Object} params - Query parameters
 * @param {number} params.page - Page number (1-based)
 * @param {number} params.limit - Items per page (default: 25)
 * @param {string} params.keyword - Search by agent name or email
 * @param {number} params.agentId - Filter by agent ID
 * @param {string} params.checkinStatuses - Filter by check-in statuses (comma-separated)
 * @param {string} params.startDepartureDate - Start departure date (YYYY-MM-DD)
 * @param {string} params.endDepartureDate - End departure date (YYYY-MM-DD)
 * @param {string} params.startScheduledTime - Start scheduled time (YYYY-MM-DD)
 * @param {string} params.endScheduledTime - End scheduled time (YYYY-MM-DD)
 * @returns {Promise<Object>} Promise resolving to Business4Flyo check-ins data with pagination
 */
export const fetchBusiness4FlyoCheckIns = async (params = { page: 1, limit: 25 }) => {
  try {
    // Build query parameters, filtering out empty values
    const queryParams = new URLSearchParams();
    
    // Set default limit if not provided
    if (!params.limit) {
      params.limit = 25;
    }
    
    // Ensure page is at least 1 (1-based pagination)
    if (!params.page || params.page < 1) {
      params.page = 1;
    }
    
    Object.entries(params).forEach(([key, value]) => {
      if (value !== null && value !== undefined && value !== '') {
        queryParams.append(key, value);
      }
    });

    const response = await fetch(flyo4BusinessAdminUrl(`/webcheckin-list?${queryParams.toString()}`), {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${getBusiness4FlyoToken()}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      // Handle 401 errors specifically
      if (response.status === 401) {
        console.error('Authentication error: Token may be invalid or expired');
        throw new Error('Authentication error: Please refresh or log in again');
      }
      throw new Error(`API error: ${response.status}`);
    }

    const data = await response.json();

    if (!data.success) {
      throw new Error(data.message || 'Failed to fetch Business4Flyo check-ins');
    }

    // Helper function to transform API data to match the expected format
    const transformData = (items) => items.map(item => {
      const ticketStatus = item.ticketStatus || item.bookingStatus || '';

      return {
        id: item.checkinAttemptId?.toString() || 'N/A',
        checkinAttemptId: item.checkinAttemptId,
        ticketId: item.ticketId,
        agentId: item.agent?.agentId,
        agentName: item.agent?.agentName || 'N/A',
        agentEmail: item.agent?.agentEmail || 'N/A',
        airline: item.airline || 'N/A',
        sector: item.sector || 'N/A',
        departureDate: item.departureDate || '',
        departureTime: item.departureTime || '',
        optInBy: item.optInBy || '',
        scheduledDate: item.scheduledDate || '',
        scheduledTime: item.scheduledTime || '',
        ticketStatus,
        bookingStatus: ticketStatus,
        checkInStatus: item.checkInStatus || '',
        checkInSubStatus: item.checkInSubStatus || null,
        checkInMessage: item.checkInMessage || '',
        boardingPass: item.boardingPass || [],
        // Combine departure date and time for display
        departureDateTime: item.departureDate && item.departureTime
          ? `${item.departureDate}T${item.departureTime}:00`
          : '',
        // Combine scheduled date and time for display
        scheduledDateTime: item.scheduledDate && item.scheduledTime
          ? `${item.scheduledDate}T${item.scheduledTime}:00`
          : '',
        // Format status for consistency with other check-ins
        status: item.checkInStatus?.toLowerCase() || 'unknown',
        sub_status: item.checkInSubStatus || null,
        statusMessage: item.checkInMessage || ''
      };
    });

    // Transform main data array
    const transformedData = transformData(data.data?.data || []);

    // Return data with pagination info
    return {
      data: transformedData,
      pagination: {
        total: data.data?.total || 0,
        page: data.data?.page || 0,
        limit: data.data?.limit || 25,
        totalPages: data.data?.totalPages || 0
      }
    };
  } catch (error) {
    console.error('Error fetching Business4Flyo check-ins:', error);
    throw error;
  }
};

/**
 * Fetch Business4Flyo ticket details by ticket ID
 * @param {string|number} ticketId - Ticket ID from route param
 * @returns {Promise<Object>} Promise resolving to ticket details
 */
export const fetchBusiness4FlyoTicketDetails = async (ticketId) => {
  const token = getBusiness4FlyoToken();
  
  const response = await fetch(
    flyo4BusinessAdminUrl(`/ticket-details/${ticketId}`),
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      }
    }
  );
  
  if (!response.ok) {
    throw new Error(`Failed to fetch ticket details: ${response.status}`);
  }
  
  const data = await response.json();
  
  if (!data.success) {
    throw new Error(data.message || 'Failed to fetch ticket details');
  }
  
  return data.data;
};

/**
 * Decode JWT token to extract agentId
 * @param {string} token - JWT token
 * @returns {number|null} Agent ID or null if not found
 */
const decodeJWT = (token) => {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(atob(base64).split('').map((c) => {
      return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
    }).join(''));
    const decoded = JSON.parse(jsonPayload);
    return decoded.agentId || null;
  } catch (error) {
    console.error('Error decoding JWT:', error);
    return null;
  }
};

/**
 * Get agentId from Business4Flyo token or provided token
 * @param {string} [token] - Optional JWT token. If not provided, uses the hardcoded token
 * @returns {number|null} Agent ID
 */
const getBusiness4FlyoAgentId = (token) => {
  const tokenToDecode = token || getBusiness4FlyoToken();
  return decodeJWT(tokenToDecode);
};

/**
 * Update ticket details
 * @param {Object} payload - Ticket update payload
 * @param {number} payload.agentId - Agent ID
 * @param {number} payload.ticketId - Ticket ID
 * @param {string} [payload.pnr] - PNR
 * @param {string} [payload.ticketStatus] - Ticket status
 * @param {string} [payload.ticketClass] - Ticket class
 * @param {string} [payload.ticketReference] - Ticket reference
 * @param {string|boolean} [payload.refundable] - Refundable status
 * @param {Array} [payload.attachments] - Attachments array
 * @returns {Promise<Object>} API response
 */
export const updateBusiness4FlyoTicket = async (payload) => {
  try {
    const token = getBusiness4FlyoToken();
    
    const response = await fetch(flyo4BusinessAdminUrl('/update'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({
        type: 'ticket',
        operation: 'update',
        payload: payload
      })
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `API error: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error updating ticket:', error);
    throw error;
  }
};

/**
 * Update check-in status
 * @param {Object} payload - Check-in update payload
 * @param {number} payload.agentId - Agent ID
 * @param {number} payload.ticketId - Ticket ID
 * @param {string} [payload.status] - Check-in status
 * @param {string} [payload.subStatus] - Sub-status
 * @param {string} [payload.statusMessage] - Status message
 * @param {string} [payload.optInBy] - Opt-in by (client|agent|traveller)
 * @returns {Promise<Object>} API response
 */
export const updateBusiness4FlyoCheckIn = async (payload) => {
  try {
    const token = getBusiness4FlyoToken();
    
    const response = await fetch(flyo4BusinessAdminUrl('/update'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({
        type: 'checkIn',
        operation: 'update',
        payload: payload
      })
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `API error: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error updating check-in:', error);
    throw error;
  }
};

/**
 * Delete traveller
 * @param {Object} payload - Delete traveller payload
 * @param {number} payload.agentId - Agent ID
 * @param {number} payload.ticketId - Ticket ID
 * @param {number} payload.flightId - Flight ID
 * @param {number} payload.travellerId - Traveller ID
 * @returns {Promise<Object>} API response
 */
export const deleteBusiness4FlyoTraveller = async (payload) => {
  try {
    const token = getBusiness4FlyoToken();
    
    const response = await fetch(flyo4BusinessAdminUrl('/update'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({
        type: 'traveller',
        operation: 'delete',
        payload: payload
      })
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `API error: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error deleting traveller:', error);
    throw error;
  }
};

/**
 * Add traveller
 * @param {Object} payload - Add traveller payload
 * @param {number} payload.clientId - Client ID
 * @param {number} payload.ticketId - Ticket ID
 * @param {string} payload.firstName - First name
 * @param {string} payload.lastName - Last name
 * @param {string} payload.countryCode - Country code
 * @param {string} payload.phone - Phone number
 * @param {string} payload.dateOfBirth - Date of birth (YYYY-MM-DD)
 * @param {string} payload.passport - Passport number
 * @param {string} payload.passportExpiry - Passport expiry (YYYY-MM-DD)
 * @param {string} payload.nationality - Nationality code
 * @param {string} payload.gender - Gender (M|F)
 * @param {Array} payload.flightData - Array of flight-specific data
 * @param {number} payload.flightData[].flightId - Flight ID
 * @param {string} payload.flightData[].seat - Seat number
 * @param {string} payload.flightData[].meal - Meal preference
 * @param {Object} payload.flightData[].baggage - Baggage info
 * @param {string} payload.flightData[].baggage.cabin - Cabin baggage
 * @param {string} payload.flightData[].baggage.checkin - Check-in baggage
 * @param {string} payload.flightData[].baggage.additional - Additional baggage
 * @returns {Promise<Object>} API response
 */
export const addBusiness4FlyoTraveller = async (payload) => {
  try {
    const token = getBusiness4FlyoToken();
    
    const response = await fetch(flyo4BusinessAdminUrl('/update'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({
        type: 'traveller',
        operation: 'add',
        payload: payload
      })
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `API error: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error adding traveller:', error);
    throw error;
  }
};

/**
 * Update traveller
 * @param {Object} payload - Update traveller payload
 * @param {number} payload.agentId - Agent ID
 * @param {number} payload.travellerId - Traveller ID (required for update)
 * @param {number} payload.clientId - Client ID
 * @param {number} payload.ticketId - Ticket ID
 * @param {string} payload.firstName - First name
 * @param {string} payload.lastName - Last name
 * @param {string} payload.countryCode - Country code
 * @param {string} payload.phone - Phone number
 * @param {string} payload.dateOfBirth - Date of birth (YYYY-MM-DD)
 * @param {string} payload.passport - Passport number
 * @param {string} payload.passportExpiry - Passport expiry (YYYY-MM-DD)
 * @param {string} payload.nationality - Nationality code
 * @param {string} payload.gender - Gender (M|F)
 * @param {Array} [payload.flightData] - Array of flight-specific data (optional)
 * @param {number} payload.flightData[].flightId - Flight ID
 * @param {string} payload.flightData[].seat - Seat number
 * @param {string} payload.flightData[].meal - Meal preference
 * @param {Object} payload.flightData[].baggage - Baggage info
 * @param {string} payload.flightData[].baggage.cabin - Cabin baggage
 * @param {string} payload.flightData[].baggage.checkin - Check-in baggage
 * @param {string} payload.flightData[].baggage.additional - Additional baggage
 * @returns {Promise<Object>} API response
 */
export const updateBusiness4FlyoTraveller = async (payload) => {
  try {
    const token = getBusiness4FlyoToken();
    
    const response = await fetch(flyo4BusinessAdminUrl('/update'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({
        type: 'traveller',
        operation: 'update',
        payload: payload
      })
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `API error: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error updating traveller:', error);
    throw error;
  }
};

/**
 * Update Business4Flyo client
 * @param {Object} payload - Client update payload
 * @param {number} payload.agentId - Agent ID
 * @param {number} payload.clientId - Client ID
 * @param {string} [payload.name] - Client name
 * @param {string} [payload.type] - Client type
 * @param {string} [payload.phone] - Phone number
 * @param {string} [payload.countryCode] - Country code
 * @param {string} [payload.companyName] - Company name
 * @param {string} [payload.lastTicketDate] - Last ticket date (YYYY-MM-DD)
 * @returns {Promise<Object>} API response
 */
export const updateBusiness4FlyoClient = async (payload) => {
  try {
    const token = getBusiness4FlyoToken();

    const response = await fetch(flyo4BusinessAdminUrl('/update'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({
        type: 'client',
        operation: 'update',
        payload
      })
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `API error: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error updating client:', error);
    throw error;
  }
};

/**
 * Update Business4Flyo agent
 * @param {Object} payload - Agent update payload
 * @param {number} payload.agentId - Agent ID
 * @param {number} payload.targetAgentId - Target agent ID
 * @param {string} [payload.name] - Agent name
 * @param {string} [payload.phone] - Phone number
 * @param {string} [payload.countryCode] - Country code
 * @param {string} [payload.callOut] - Agent call-out text
 * @returns {Promise<Object>} API response
 */
export const updateBusiness4FlyoAgent = async (payload) => {
  try {
    const token = getBusiness4FlyoToken();

    const response = await fetch(flyo4BusinessAdminUrl('/update'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({
        type: 'agent',
        operation: 'update',
        payload
      })
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `API error: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error updating agent:', error);
    throw error;
  }
};

/**
 * Fetch email text for a ticket
 * @param {number} ticketId - Ticket ID
 * @param {number} agentId - Agent ID
 * @returns {Promise<Object>} API response with email text
 */
export const fetchBusiness4FlyoEmailText = async (ticketId, agentId) => {
  try {
    const token = getBusiness4FlyoToken();
    
    const response = await fetch(
      business4FlyoAdminUrl(`/fetchEmailText/${ticketId}/${agentId}`),
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `API error: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error fetching email text:', error);
    throw error;
  }
};

/**
 * Upload ticket document
 * @param {FormData} formData - Form data with file, agentId, ticketId, fileName, userType
 * @param {string} formData.userType - User type ('customer' or 'supplier')
 * @returns {Promise<Object>} API response
 */
export const uploadBusiness4FlyoDocument = async (formData) => {
  try {
    const token = getBusiness4FlyoToken();
    
    const response = await fetch(flyo4BusinessAdminUrl('/uploadTicketDocument'), {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`
        // Don't set Content-Type for FormData, browser will set it with boundary
      },
      body: formData
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `API error: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error uploading document:', error);
    throw error;
  }
};

/**
 * Resolve an ambiguous boarding pass traveller match
 * @param {Object} params - Resolution parameters
 * @param {string} params.resolutionToken - Token returned by uploadTicketDocument
 * @param {number} params.travellerId - Selected booking traveller ID
 * @param {number|string} params.ticketId - Ticket ID
 * @param {number|string} params.agentId - Agent ID
 * @returns {Promise<Object>} API response
 */
export const resolveBusiness4FlyoBoardingPassTraveller = async ({
  resolutionToken,
  travellerId,
  ticketId,
  agentId
}) => {
  try {
    const token = getBusiness4FlyoToken();

    const response = await fetch(flyo4BusinessAdminUrl('/boarding-pass/resolve-traveller'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({
        resolutionToken,
        travellerId,
        ticketId,
        agentId
      })
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `API error: ${response.status}`);
    }

    const data = await response.json();

    if (!data.success) {
      throw new Error(data.message || 'Failed to assign boarding pass');
    }

    return data;
  } catch (error) {
    console.error('Error assigning boarding pass:', error);
    throw error;
  }
};

/**
 * Copy ticket document between customer and supplier
 * @param {Object} params - Copy parameters
 * @param {number} params.agentId - Agent ID
 * @param {number} params.ticketId - Ticket ID
 * @param {string} params.userType - Target user type ('customer' | 'supplier')
 * @param {Object} params.attachment - Document attachment to copy
 * @returns {Promise<Object>} API response
 */
export const copyBusiness4FlyoDocument = async ({ agentId, ticketId, userType, attachment }) => {
  try {
    const token = getBusiness4FlyoToken();
    
    const response = await fetch(flyo4BusinessAdminUrl('/update'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({
        type: 'ticketDocument',
        operation: 'update',
        payload: {
          agentId,
          ticketId,
          userType,
          attachments: [attachment]
        }
      })
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `API error: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error copying document:', error);
    throw error;
  }
};

/**
 * Update ticket documents with attachments array for a specific user type
 * @param {Object} params - Update parameters
 * @param {number} params.agentId - Agent ID
 * @param {number} params.ticketId - Ticket ID
 * @param {string} params.userType - User type ('customer' | 'supplier')
 * @param {Array} params.attachments - Attachments array
 * @returns {Promise<Object>} API response
 */
export const updateBusiness4FlyoTicketDocuments = async ({ agentId, ticketId, userType, attachments }) => {
  try {
    const token = getBusiness4FlyoToken();

    const response = await fetch(flyo4BusinessAdminUrl('/update'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({
        type: 'ticketDocument',
        operation: 'update',
        payload: {
          agentId,
          ticketId,
          userType,
          attachments
        }
      })
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `API error: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error updating ticket documents:', error);
    throw error;
  }
};

// Export agentId getter for use in components
export { getBusiness4FlyoAgentId };

/**
 * Fetch all check-ins from the API
 * @param {Object} params - Query parameters
 * @param {number} params.page - Page number (0-based)
 * @param {string} params.email - Filter by email
 * @param {number} params.userId - Filter by user ID
 * @param {string} params.checkinStatus - Filter by check-in status
 * @param {string} params.departureDateMin - Minimum departure date (YYYY-MM-DD)
 * @param {string} params.departureDateMax - Maximum departure date (YYYY-MM-DD)
 * @param {string} params.scheduledAtMin - Minimum scheduled time (YYYY-MM-DD)
 * @param {string} params.scheduledAtMax - Maximum scheduled time (YYYY-MM-DD)
 * @returns {Promise<Object>} Promise resolving to check-ins data with pagination
 */
export const fetchCheckIns = async (params = { page: 0 }) => {
  try {
    // Build query parameters, filtering out empty values
    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== null && value !== undefined && value !== '') {
        queryParams.append(key, value);
      }
    });

    const response = await fetch(`${API_BASE_URL}/getCheckinAttempts?${queryParams.toString()}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${getAuthToken()}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      // Handle 401 errors specifically
      if (response.status === 401) {
        console.error('Authentication error: Token may be invalid or expired');
        throw new Error('Authentication error: Please refresh or log in again');
      }
      throw new Error(`API error: ${response.status}`);
    }

    const data = await response.json();

    if (!data.success) {
      throw new Error(data.message || 'Failed to fetch check-ins');
    }

    // Helper function to transform API data to match the expected format in the application
    const transformData = (items) => items.map(item => ({
      id: item.id.toString(),
      passengerName: 'N/A', // Not provided in API
      flightNumber: item.flightNumbers?.length > 0 ? item.flightNumbers[0] : 'N/A',
      origin: item.ticket?.source_iata || 'N/A',
      destination: item.ticket?.destination_iata || 'N/A',
      sourceTimezone: item.ticket?.sourceTimezone || '',
      departureDate: item.ticket?.departure_date || '',
      checkInTime: item.scheduled_time || '', // Use scheduled_time instead of attempted_time
      attemptedTime: item.attempted_time || '', // Keep attempted_time as separate field
      scheduledTime: item.scheduled_time || '', // Add scheduled_time field
      status: (item.status || '').toLowerCase(),
      sub_status: item.sub_status || null,
      boardingPass: item.status === 'COMPLETED',
      seatNumber: null, // Not provided in API
      gate: null, // Not provided in API
      boardingTime: null, // Not provided in API
      baggage: [],
      pnr: item.ticket?.pnr || 'N/A',
      statusMessage: item.status_message || '',
      ticket_id: item.ticket_id,
      user_id: item.user_id,
      mobileNumber: item.mobileNumber || '',
      emails: item.emails || [],
      // Additional fields from new API
      arrivalDate: item.ticket?.arrival_date || '',
      checkInClosingTime: item.ticket?.check_in_closing_time || '',
      departureDates: item.departureDates || [],
      flightPassengerStats: item.flightPassengerStats || []
    }));

    // Transform main data array
    const transformedData = transformData(data.data);

    // Transform sortedData arrays if they exist
    const sortedData = data.sortedData ? {
      departureDateAsc: transformData(data.sortedData.departureDateAsc || []),
      scheduledDateAsc: transformData(data.sortedData.scheduledDateAsc || []),
      departureDateDesc: transformData(data.sortedData.departureDateDesc || []),
      scheduledDateDesc: transformData(data.sortedData.scheduledDateDesc || [])
    } : null;

    // Return data with pagination info and sortedData
    return {
      data: transformedData,
      sortedData: sortedData,
      pagination: data.pagination
    };
  } catch (error) {
    console.error('Error fetching check-ins:', error);
    throw error;
  }
};

/**
 * Fetch web check-in dashboard metrics
 * @param {Object} params - Query parameters
 * @param {string} params.startDate - Start date in YYYY-MM-DD
 * @param {string} params.endDate - End date in YYYY-MM-DD
 * @returns {Promise<Object>} Promise resolving to dashboard metrics payload
 */
export const fetchCheckinDashboardMetrics = async ({
  startDate,
  endDate
}) => {
  try {
    const queryParams = new URLSearchParams();
    queryParams.append('startDate', startDate);
    queryParams.append('endDate', endDate);

    const response = await fetch(`${API_BASE_URL}/checkin/dashboardMetrics?${queryParams.toString()}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${getAuthToken()}`,
        'Content-Type': 'application/json'
      }
    });

    const result = await response.json().catch(() => ({}));

    if (!response.ok || !result.success) {
      const error = new Error(result.message || `API error: ${response.status}`);
      error.status = response.status;
      throw error;
    }

    return result.data;
  } catch (error) {
    console.error('Error fetching check-in dashboard metrics:', error);
    throw error;
  }
};

const fetchSla = async (path, params) => {
  const queryParams = new URLSearchParams();
  Object.entries(params || {}).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return;
    queryParams.append(key, Array.isArray(value) ? value.join(',') : String(value));
  });

  const response = await fetch(`${API_BASE_URL}${path}?${queryParams.toString()}`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${getAuthToken()}`,
      'Content-Type': 'application/json'
    }
  });

  const result = await response.json().catch(() => ({}));

  if (!response.ok || !result.success) {
    const error = new Error(result.message || `API error: ${response.status}`);
    error.status = response.status;
    throw error;
  }

  return result.data;
};

/**
 * Fetch internal web check-in SLA numbers (devs and ops).
 * @param {Object} params - Query parameters
 * @param {string} params.from - First departure date, YYYY-MM-DD
 * @param {string} params.to - Last departure date, YYYY-MM-DD
 * @param {string[]} [params.flows] - Any of core, businessFlyo, extPartner
 * @param {string} [params.airline] - IATA airline code
 * @param {string[]} [params.actors] - Whose work counts: automation, ops (default both)
 * @param {number} [params.completionMinutes] - Scheduled → completed threshold
 * @param {number} [params.hiddenFailureHours] - How long a hidden failure may wait for ops
 * @param {number} [params.targetCompletionRate] - Completion target, percent
 * @param {number} [params.targetOnTimeRate] - Share done within completionMinutes, percent
 * @returns {Promise<Object>} Promise resolving to the SLA report
 */
export const fetchWebcheckinSla = async (params) => {
  try {
    return await fetchSla('/webcheckin/sla', params);
  } catch (error) {
    console.error('Error fetching web check-in SLA:', error);
    throw error;
  }
};

/**
 * Fetch one page of an SLA list, for "show all".
 * @param {Object} params - The report's query parameters, plus:
 * @param {string} params.list - opsQueue, overdueQueue or slowest
 * @param {number} [params.page] - 1-based page
 * @param {number} [params.pageSize] - Rows per page (default 50, at most 100)
 * @returns {Promise<Object>} Promise resolving to { list, page, pageSize, total, items }
 */
export const fetchWebcheckinSlaList = async (params) => {
  try {
    return await fetchSla('/webcheckin/sla/list', params);
  } catch (error) {
    console.error('Error fetching web check-in SLA list:', error);
    throw error;
  }
};

/**
 * Fetch one page of every check-in behind the SLA numbers, for the records table.
 * @param {Object} params - The report's query parameters, plus:
 * @param {number} [params.page] - 1-based page
 * @param {number} [params.pageSize] - Rows per page (default 50, at most 100)
 * @param {string} [params.sort] - toComplete, passLead, departure, scheduled, completed, boardingPass, status, airline, flow or booking
 * @param {string} [params.order] - asc or desc
 * @returns {Promise<Object>} Promise resolving to { page, pageSize, total, sort, order, items }
 */
export const fetchWebcheckinSlaRecords = async (params) => {
  try {
    return await fetchSla('/webcheckin/sla/records', params);
  } catch (error) {
    console.error('Error fetching web check-in SLA records:', error);
    throw error;
  }
};

/**
 * Fetch a single check-in by ID
 * @param {string} id - Check-in ID
 * @returns {Promise<Object>} Promise resolving to check-in object
 */
export const fetchCheckInById = async (id) => {
  try {
    const response = await fetch(`${API_BASE_URL}/checkin/getCheckinAttempt/${id}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${getAuthToken()}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error(`API error: ${response.status}`);
    }

    const data = await response.json();

    if (!data.success) {
      throw new Error(data.message || `Failed to load check-in ${id}`);
    }

    const item = data.data;

    // Transform API data to match the expected format in the application
    return {
      id: item.id.toString(),
      passengerName: 'N/A', // Not provided in API
      flightNumber: item.flightNumbers?.length > 0 ? item.flightNumbers[0] : 'N/A',
      origin: item.ticket?.source_iata || 'N/A',
      destination: item.ticket?.destination_iata || 'N/A',
      departureDate: item.ticket?.departure_date || new Date().toISOString(),
      checkInTime: item.attempted_time,
      status: (item.status || '').toLowerCase(),
      boardingPass: item.status === 'COMPLETED',
      seatNumber: null, // Not provided in API
      gate: null, // Not provided in API
      boardingTime: null, // Not provided in API
      baggage: [],
      pnr: item.ticket?.pnr || 'N/A',
      statusMessage: item.status_message || '',
      ticket_id: item.ticket_id,
      user_id: item.user_id,
      mobileNumber: item.mobileNumber,
      emails: item.emails || []
    };
  } catch (error) {
    console.error(`Error fetching check-in ${id}:`, error);
    throw error;
  }
};

/**
 * Fetch ticket data by ticket ID
 * @param {string} ticket_id - Ticket ID
 * @returns {Promise<Object>} Promise resolving to ticket data
 */
export const fetchTicketData = async (ticket_id,user_id) => {
  try {
    // Log the ticket ID to debug
    console.log('Fetching ticket data with ID:', ticket_id);
    console.log('fetching ticket data using user_id: ',user_id)

    const response = await fetch(`${API_BASE_URL}/checkin/getTicketData?ticketId=${ticket_id}&userId=${user_id}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${getAuthToken()}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error(`API error: ${response.status}`);
    }

    const data = await response.json();

    if (!data.success) {
      throw new Error(data.message || `Failed to load ticket data for ticket ID ${ticket_id}`);
    }

    return data.data;
  } catch (error) {
    console.error(`Error fetching ticket data for ticket ID ${ticket_id}:`, error);
    throw error;
  }
};

/**
 * Fetch email text data by ticket ID and user ID
 * @param {string} ticketId - Ticket ID
 * @param {string} userId - User ID
 * @returns {Promise<Object>} Promise resolving to email text data
 */
export const fetchEmailTextData = async (ticketId, userId) => {
  try {
    console.log('Fetching email text data with ticketId:', ticketId, 'userId:', userId);

    const response = await fetch(adminUrl(`/getEmailTextData?ticketId=${ticketId}&userId=${userId}`), {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${getAuthToken()}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error(`API error: ${response.status}`);
    }

    const data = await response.json();

    if (!data.success) {
      throw new Error(data.message || `Failed to load email text data for ticket ID ${ticketId}`);
    }

    return data.data;
  } catch (error) {
    console.error(`Error fetching email text data for ticket ID ${ticketId}:`, error);
    throw error;
  }
};

/**
 * Fetch parsed email text from internal parse-text API
 * @param {string} ticketId - Ticket ID
 * @param {string} userId - User ID
 * @returns {Promise<Object>} Promise resolving to parsed email text data
 */
export const fetchParsedEmailText = async (ticketId, userId) => {
  try {
    console.log('Fetching parsed email text with ticketId:', ticketId, 'userId:', userId);

    const response = await fetch(internalApiV2Url('/parse-text'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        userId: userId,
        ticketId: ticketId
      })
    });

    if (!response.ok) {
      throw new Error(`API error: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error(`Error fetching parsed email text for ticket ID ${ticketId}:`, error);
    throw error;
  }
};

/**
 * Update a check-in
 * @param {string} id - Check-in ID
 * @param {Object} updates - Object with properties to update
 * @returns {Promise<Object>} Promise resolving to updated check-in
 */
export const updateCheckIn = async (id, updates) => {
  // Simulate API call
  await delay(1000);

  const checkInIndex = checkIns.findIndex(ci => ci.id === id);

  if (checkInIndex === -1) {
    throw new Error(`Check-in with ID ${id} not found`);
  }

  // In a real app, this would be a server-side update
  // For demo purposes, we're just returning the merged object
  const updatedCheckIn = {
    ...checkIns[checkInIndex],
    ...updates
  };

  return updatedCheckIn;
};

/**
 * Upload a boarding pass
 * @param {string} checkInId - Check-in ID
 * @param {File} file - Boarding pass file
 * @returns {Promise<Object>} Promise resolving to updated check-in
 */
export const uploadBoardingPass = async (checkInId, file) => {
  // Simulate API call
  await delay(1500);

  // In a real app, this would upload the file to a server
  console.log(`Uploading boarding pass for check-in ${checkInId}:`, file.name);

  return {
    success: true,
    message: 'Boarding pass uploaded successfully',
    fileName: file.name
  };
};

/**
 * Add a new passenger to a flight
 * @param {Object} data - Passenger data
 * @param {number} data.flightId - Flight ID
 * @param {number} data.userId - User ID
 * @param {number} data.ticketId - Ticket ID
 * @param {string} data.firstName - Passenger first name
 * @param {string} data.lastName - Passenger last name
 * @param {string} [data.seat] - Passenger seat number
 * @param {string} [data.meal] - Passenger meal preference
 * @param {string} [data.checkinBaggage] - Check-in baggage allowance
 * @param {string} [data.cabinBaggage] - Cabin baggage allowance
 * @param {string} [data.sequenceNumber] - Passenger sequence number
 * @returns {Promise<Object>} Promise resolving to API response
 */
export const addPassenger = async (data) => {
  try {
    console.log('passenger addition is: ',data)
    const response = await fetch(`${API_BASE_URL}/update`, {
      method: 'PATCH',
      headers: {
        'Authorization': `Bearer ${getAuthToken()}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        type: "PASSENGER",
        operationType: "ADD",
        body: data
      })
    });

    const result = await response.json();
    console.log('response: ', result);

    // const result = await response.json();

    if (!result.success) {
      throw new Error(result.message || 'Failed to add passenger');
    }

    return result;
  } catch (error) {
    console.error('Error adding passenger:', error);
    throw error;
  }
};

export const triggerBrowserUserForCheckIn = async (userId, ticketId, scheduledTime = null) => {
  try {
    console.log(`triggering browser use for ids: userId: ${userId} and ticketId: ${ticketId}${scheduledTime ? `, scheduledTime: ${scheduledTime}` : ''}`)

    let url = `${API_BASE_URL}/triggerCheckinFlow?ticketId=${ticketId}&userId=${userId}`;

    // Add scheduledTime parameter if provided
    if (scheduledTime) {
      url += `&scheduledTime=${encodeURIComponent(scheduledTime)}`;
    }

    const response = await fetch(url, {
      method: 'GET', // curl uses GET by default
      headers: {
        'Authorization': `Bearer ${getAuthToken()}`, // assuming getAuthToken() returns the right token
      }
    });

    const result = await response.json();
    console.log('response: ', result);

    if (!result.success) {
      throw new Error(result.message || 'Failed to trigger check-in flow');
    }

    return result;
  } catch (error) {
    console.error('Error triggering check-in flow:', error);
    throw error;
  }
};

/**
 * Update check-in status manually
 * @param {number} ticketId - Ticket ID
 * @param {number} userId - User ID
 * @param {string} status - Status to set (IN_PROGRESS, COMPLETED, FAILED, SCHEDULED)
 * @returns {Promise<Object>} Promise resolving to API response
 */
export const updateCheckInStatus = async (ticketId, userId, status, subStatus = '', statusMessage = '') => {
  try {
    console.log(`Updating check-in status: ticketId: ${ticketId}, userId: ${userId}, status: ${status}`);

    const response = await fetch(`${API_BASE_URL}/update`, {
      method: 'PATCH',
      headers: {
        'Authorization': `Bearer ${getAuthToken()}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        type: "WEB_CHECKIN",
        operationType: "UPDATE",
        body: {
          ticketId: ticketId,
          userId: userId,
          status: status,
          subStatus: subStatus || '',
          statusMessage: statusMessage || ''
        },
      }),
    });

    const result = await response.json();
    console.log('Update status response:', result);

    if (!result.success) {
      throw new Error(result.message || `Failed to update check-in status to ${status}`);
    }

    return result;
  } catch (error) {
    console.error(`Error updating check-in status to ${status}:`, error);
    throw error;
  }
};

/**
 * Fetch flights data from the API
 * @param {Object} params - Query parameters
 * @param {number} params.page - Page number
 * @param {number} params.itemsPerPage - Items per page
 * @param {number} params.userId - Filter by user ID
 * @param {number} params.ticketId - Filter by ticket ID
 * @param {string} params.email - Filter by email
 * @param {string} params.pnr - Filter by PNR (Passenger Name Record)
 * @param {string} params.departureDateMin - Minimum departure date (YYYY-MM-DD)
 * @param {string} params.departureDateMax - Maximum departure date (YYYY-MM-DD)
 * @returns {Promise<Array>} Promise resolving to array of flight data
 */
export const fetchFlightsData = async (params = { page: 1 }) => {
  try {
    // Convert userId and ticketId to numbers if they exist
    if (params.userId) {
      params.userId = Number(params.userId);
    }
    if (params.ticketId) {
      params.ticketId = Number(params.ticketId);
    }
    params.page=params.page-1;

    const queryParams = new URLSearchParams();

    // Add parameters to query string
    Object.entries(params).forEach(([key, value]) => {
      if (value !== null && value !== undefined && value !== '') {
        queryParams.append(key, value);
      }
    });

    const token = checkAndRefreshToken();

    const response = await fetch(`${API_BASE_URL}/fetchFlightsData?${queryParams.toString()}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    console.log('query params are: ', queryParams.toString());

    if (!response.ok) {
      // Handle 401 errors specifically
      if (response.status === 401) {
        console.error('Authentication error: Token may be invalid or expired');
        // Clear existing token and force a refresh
        localStorage.removeItem('authToken');
        localStorage.removeItem('loginTimestamp');
        // Get a fresh token
        const freshToken = getAuthToken();
        console.log('Retrying with fresh token:', freshToken ? 'Token exists' : 'No token');
        // Retry the request once with the new token
        return await fetchFlightsData(params);
      }
      throw new Error(`API error: ${response.status}`);
    }

    const data = await response.json();

    if (!data.success) {
      throw new Error(data.message || 'Failed to fetch flights data');
    }



    return data.data || [];
  } catch (error) {
    console.error('Error fetching flights data:', error);
    // Provide more detailed error message
    if (error.message.includes('401')) {
      throw new Error('Authentication failed. Please log out and log in again.');
    } else if (error.message.includes('404')) {
      throw new Error('The requested data could not be found. Please check your filters.');
    } else if (error.message.includes('500')) {
      throw new Error('Server error. Please try again later.');
    } else {
      throw error;
    }
  }
};

/**
 * Delete a user ticket
 * @param {number} ticketId - Ticket ID
 * @param {number} userId - User ID
 * @returns {Promise<Object>} Promise resolving to API response
 */
export const deleteUserTicket = async (ticketId, userId) => {
  try {
    console.log(`Deleting user ticket: ticketId: ${ticketId}, userId: ${userId}`);

    const response = await fetch(`${API_BASE_URL}/update`, {
      method: 'PATCH',
      headers: {
        'Authorization': `Bearer ${getAuthToken()}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        type: "USER_TICKET",
        operationType: "UPDATE",
        body: {
          ticketId: ticketId,
          userId: userId,
          deleted: true
        }
      })
    });

    const result = await response.json();
    console.log('Delete ticket response:', result);

    if (!result.success) {
      throw new Error(result.message || 'Failed to delete ticket');
    }

    return result;
  } catch (error) {
    console.error('Error deleting ticket:', error);
    throw error;
  }
};

/**
 * Cancel a user ticket
 * @param {number} ticketId - Ticket ID
 * @param {number} userId - User ID
 * @returns {Promise<Object>} Promise resolving to API response
 */
export const cancelUserTicket = async (ticketId, userId) => {
  try {
    console.log(`Cancelling user ticket: ticketId: ${ticketId}, userId: ${userId}`);

    const response = await fetch(`${API_BASE_URL}/update`, {
      method: 'PATCH',
      headers: {
        'Authorization': `Bearer ${getAuthToken()}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        type: "USER_TICKET",
        operationType: "UPDATE",
        body: {
          ticketId: ticketId,
          userId: userId,
          cancelled: true
        }
      })
    });

    const result = await response.json();
    console.log('Cancel ticket response:', result);

    if (!result.success) {
      throw new Error(result.message || 'Failed to cancel ticket');
    }

    return result;
  } catch (error) {
    console.error('Error cancelling ticket:', error);
    throw error;
  }
};

/**
 * Delete a ticket document
 * @param {number} ticketDocumentId - Ticket Document ID
 * @returns {Promise<Object>} Promise resolving to API response
 */
export const deleteTicketDocument = async (ticketDocumentId) => {
  try {
    console.log(`Deleting ticket document with ID: ${ticketDocumentId}`);

    const response = await fetch(`${API_BASE_URL}/update`, {
      method: 'PATCH',
      headers: {
        'Authorization': `Bearer ${getAuthToken()}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        type: "TICKET_DOCUMENT",
        operationType: "DELETE",
        body: {
          ticketDocumentId: ticketDocumentId
        }
      })
    });

    const result = await response.json();
    console.log('Delete ticket document response:', result);

    if (!result.success) {
      throw new Error(result.message || 'Failed to delete ticket document');
    }

    return result;
  } catch (error) {
    console.error('Error deleting ticket document:', error);
    throw error;
  }
};

/**
 * Generate JWT for a user
 * @param {number} userId - User ID
 * @returns {Promise<Object>} Promise resolving to JWT token
 */
/**
 * Fetch user data from the API
 * @param {Object} params - Query parameters
 * @param {number} params.userId - Optional user ID to filter by
 * @param {string} params.email - Optional email to filter by
 * @param {number} params.page - Page number (default: 0)
 * @param {number} params.limit - Number of items per page (default: 50)
 * @returns {Promise<Object>} Promise resolving to user data and pagination info
 */
export const getUserData = async (params = {}) => {
  try {
    const queryParams = new URLSearchParams();

    // Add parameters to query string if they exist
    if (params.userId) {
      queryParams.append('userId', params.userId);
    }

    if (params.email) {
      queryParams.append('email', params.email);
    }

    // Add pagination parameters with defaults
    queryParams.append('page', params.page !== undefined ? params.page : 0);
    queryParams.append('limit', params.limit !== undefined ? params.limit : 50);

    const response = await fetch(`${API_BASE_URL}/getUserData?${queryParams.toString()}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${getAuthToken()}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      // Handle 401 errors specifically
      if (response.status === 401) {
        console.error('Authentication error: Token may be invalid or expired');
        throw new Error('Authentication error: Please refresh or log in again');
      }
      throw new Error(`API error: ${response.status}`);
    }

    const data = await response.json();

    if (!data.success) {
      throw new Error(data.message || 'Failed to fetch user data');
    }

    return data.data;
  } catch (error) {
    console.error('Error fetching user data:', error);
    throw error;
  }
};

export const generateUserJwt = async (userId) => {
  try {
    console.log(`Generating JWT for user ID: ${userId}`);

    const response = await fetch(internalApiV1Url(`/jwt?userId=${userId}`), {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error(`API error: ${response.status}`);
    }

    const data = await response.json();
    console.log('JWT response:', data);

    // The API returns the JWT directly in the response with a jwt property
    if (!data.jwt) {
      throw new Error(`Failed to generate JWT for user ID ${userId}`);
    }

    // Return the JWT token in the expected format
    return {
      success: true,
      data: { token: data.jwt }
    };
  } catch (error) {
    console.error('Error generating JWT:', error);
    throw error;
  }
};

/**
 * Fetch user specific information from the API
 * @param {number} userId - User ID to get specific information for
 * @returns {Promise<Object>} Promise resolving to user specific data
 */
export const userSpecificInfo = async (userId) => {
  try {
    console.log(`Fetching specific information for user ID: ${userId}`);

    const response = await fetch(`${API_BASE_URL}/getUserSpecificInfo?userId=${userId}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${getAuthToken()}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      // Handle 401 errors specifically
      if (response.status === 401) {
        console.error('Authentication error: Token may be invalid or expired');
        throw new Error('Authentication error: Please refresh or log in again');
      }
      throw new Error(`API error: ${response.status}`);
    }

    const data = await response.json();

    if (!data.success) {
      throw new Error(data.message || `Failed to fetch specific information for user ID ${userId}`);
    }

    return data.data;
  } catch (error) {
    console.error(`Error fetching specific information for user ID ${userId}:`, error);
    throw error;
  }
};

/**
 * Fetch user check-in preferences by user ID
 * @param {string|number} userId - User ID
 * @returns {Promise<Object>} Promise resolving to { seat_position, row_position }
 */
export const fetchUserPreferences = async (userId) => {
  try {
    const response = await fetch(`${API_BASE_URL}/checkin/getUserPreferences?userId=${userId}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${getAuthToken()}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error(`API error: ${response.status}`);
    }

    const data = await response.json();

    if (!data.success) {
      throw new Error(data.message || `Failed to load user preferences for user ID ${userId}`);
    }

    return data.data;
  } catch (error) {
    console.error(`Error fetching user preferences for user ID ${userId}:`, error);
    throw error;
  }
};

/**
 * Update user information
 * @param {Object} userFormData - User form data
 * @param {string} userFormData.firstName - User's first name
 * @param {string} userFormData.lastName - User's last name
 * @param {string} userFormData.mobileNumber - User's mobile number
 * @param {number} userId - User ID to update
 * @returns {Promise<Object>} Promise resolving to API response
 */
export const updateUserInfo = async (userFormData, userId) => {
  try {
    console.log(`Updating user info for user ID: ${userId}`, userFormData);

    const response = await fetch(`${API_BASE_URL}/update`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${getAuthToken()}`,
      },
      body: JSON.stringify({
        type: "USER",
        operationType: "UPDATE",
        body: {
          userId: userId,
          firstName: userFormData.firstName,
          lastName: userFormData.lastName,
          mobileNumber: userFormData.mobileNumber || "",
        },
      }),
    });

    const result = await response.json();
    console.log('Update user info response:', result);

    if (!result.success) {
      throw new Error(result.message || 'Failed to update user information');
    }

    return result;
  } catch (error) {
    console.error('Error updating user information:', error);
    throw error;
  }
};

/**
 * Get trips/bookings from the API
 * @param {Object} params - Query parameters
 * @param {number} [params.userId] - User ID to filter by
 * @param {string} [params.status] - Booking status to filter by (e.g., 'BOOKING_INITIALISED')
 * @param {string} [params.paymentStatus] - Payment transaction status to filter by ('INITIATED' | 'PROCESSING' | 'COMPLETED' | 'FAILED')
 * @param {string} [params.createdAt] - Created date to filter by (YYYY-MM-DD format)
 * @param {string} [params.departureDate] - Departure date to filter by (YYYY-MM-DD format)
 * @param {number} [params.limit] - Number of results to return (default: 25)
 * @param {number} [params.offset] - Offset for pagination (default: 0)
 * @returns {Promise<Object>} Promise resolving to API response with trips data
 */
export const getTrips = async (params = {}) => {
  try {
    // Build query string from parameters
    const queryParams = new URLSearchParams();

    if (params.userId) queryParams.append('userId', params.userId);
    if (params.status) queryParams.append('status', params.status);
    if (params.paymentStatus) queryParams.append('paymentStatus', params.paymentStatus);
    if (params.createdAt) queryParams.append('createdAt', params.createdAt);
    if (params.departureDate) queryParams.append('departureDate', params.departureDate);
    if (params.limit) queryParams.append('limit', params.limit);
    if (params.offset) queryParams.append('offset', params.offset);

    const url = tripUrl(`?${queryParams.toString()}`);
    console.log('Fetching trips from:', url);

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJ1c2VySWQiOjQxNiwiaWF0IjoxNzUzOTYxNzE4fQ.mENLunmu6zzeSBCbKvpft8GzTMvuMGlc_CLu6eRFSXM`
      }
    });

    if (!response.ok) {
      throw new Error(`API error: ${response.status} ${response.statusText}`);
    }

    const result = await response.json();
    console.log('Get trips response:', result);

    return result;
  } catch (error) {
    console.error('Error fetching trips:', error);
    throw error;
  }
};

/**
 * Get trip details by trip ID
 * @param {string} tripId - Trip ID to fetch details for
 * @returns {Promise<Object>} Promise resolving to trip details
 */
export const getTripDetails = async (tripId) => {
  try {
    console.log('Fetching trip details for ID:', tripId);

    const response = await fetch(tripUrl(`/${tripId}`), {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJ1c2VySWQiOjQxNiwiaWF0IjoxNzUzOTYxNzE4fQ.mENLunmu6zzeSBCbKvpft8GzTMvuMGlc_CLu6eRFSXM`
      }
    });

    if (!response.ok) {
      throw new Error(`API error: ${response.status} ${response.statusText}`);
    }

    const result = await response.json();
    console.log('Get trip details response:', result);

    return result;
  } catch (error) {
    console.error('Error fetching trip details:', error);
    throw error;
  }
};

/**
 * Update a trip's status and related fields
 * @param {string|number} tripId
 * @param {{
 *   status?: string,
 *   pnr?: string,
 *   bookingReference?: string,
 *   fareType?: string,
 *   ticketClass?: string,
 *   checkinBaggage?: { weight: number, weightUnit: string },
 *   cabinBaggage?: { weight: number, weightUnit: string },
 *   additionalNotes?: string
 * }} payload
 * @returns {Promise<Object>} API response
 */
export const updateTripStatus = async (tripId, payload) => {
  try {
    console.log('Updating trip', tripId, 'with payload:', payload);
    const response = await fetch(tripUrl(`/${tripId}`), {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJ1c2VySWQiOjQxNiwiaWF0IjoxNzUzOTYxNzE4fQ.mENLunmu6zzeSBCbKvpft8GzTMvuMGlc_CLu6eRFSXM`
      },
      body: JSON.stringify(payload)
    });

    const result = await response.json();
    if (!response.ok) {
      throw new Error(result?.message || `API error: ${response.status} ${response.statusText}`);
    }
    return result;
  } catch (error) {
    console.error('Error updating trip:', error);
    throw error;
  }
};

/**
 * Update PNR for a trip
 * @param {string|number} tripId - Trip ID
 * @param {string} pnr - New PNR value
 * @returns {Promise<Object>} API response
 */
export const updateTripPnr = async (tripId, pnr) => {
  try {
    console.log('Updating PNR for trip', tripId, 'with PNR:', pnr);
    const response = await fetch(tripUrl(`/${tripId}`), {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJ1c2VySWQiOjQxNiwiaWF0IjoxNzUzOTYxNzE4fQ.mENLunmu6zzeSBCbKvpft8GzTMvuMGlc_CLu6eRFSXM`
      },
      body: JSON.stringify({ pnr: pnr })
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `API error: ${response.status}`);
    }

    const data = await response.json();
    console.log('PNR update response:', data);
    return data;
  } catch (error) {
    console.error('Error updating PNR:', error);
    throw error;
  }
};

/**
 * Add a new trip/flight for a user
 * @param {Object} tripData - Trip data
 * @param {number} tripData.userId - User ID
 * @param {string} tripData.flightNumber - Flight number (required)
 * @param {string} tripData.departureDate - Departure date in YYYY-MM-DD format (required)
 * @param {string} [tripData.departureIata] - Departure IATA code (optional)
 * @param {string} [tripData.arrivalIata] - Arrival IATA code (optional)
 * @returns {Promise<Object>} Promise resolving to API response
 */
export const addTrip = async (tripData) => {
  try {
    console.log('Adding new trip:', tripData);

    const response = await fetch('https://dev-api.havahavai.com/core/v2/internal/addTrip', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        userId: tripData.userId,
        flightNumber: tripData.flightNumber,
        departureDate: tripData.departureDate,
        departureIata: tripData.departureIata || "",
        arrivalIata: tripData.arrivalIata || ""
      })
    });

    const result = await response.json();
    console.log('Add trip response:', result);

    // For 400 and 500 status codes, throw the error message as-is
    if (!response.ok) {
      if (response.status === 400 || response.status === 500) {
        throw new Error(result.message || `API error: ${response.status}`);
      }
      throw new Error(`API error: ${response.status}`);
    }

    return result;
  } catch (error) {
    console.error('Error adding trip:', error);
    throw error;
  }
};

/**
 * Update passenger data using passengerFlightId
 * @param {Object} passengerData - Passenger data to update
 * @param {number} passengerData.passengerFlightId - Passenger Flight ID (required)
 * @param {string} [passengerData.firstName] - Passenger first name
 * @param {string} [passengerData.lastName] - Passenger last name
 * @param {string} [passengerData.gender] - Passenger gender
 * @param {string} [passengerData.date_of_birth] - Passenger date of birth
 * @returns {Promise<Object>} Promise resolving to API response
 */
export const updatePassengerData = async (passengerData) => {
  try {
    console.log('Updating passenger data:', passengerData);

    const response = await fetch(`${API_BASE_URL}/update`, {
      method: 'PATCH',
      headers: {
        'Authorization': `Bearer ${getAuthToken()}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        type: "PASSENGER",
        operationType: "UPDATE",
        body: {
          passengerFlightId: passengerData.passengerFlightId,
          firstName: passengerData.firstName || "",
          lastName: passengerData.lastName || "",
          gender: passengerData.gender || "",
          date_of_birth: passengerData.date_of_birth || ""
        }
      })
    });

    const result = await response.json();
    console.log('Update passenger response:', result);

    if (!result.success) {
      throw new Error(result.message || 'Failed to update passenger data');
    }

    return result;
  } catch (error) {
    console.error('Error updating passenger data:', error);
    throw error;
  }
};

/**
 * Update individual passenger data using the INDIVIDUAL_PASSENGER endpoint
 * @param {Object} passengerData - Passenger data object
 * @param {string} passengerData.passengerId - Individual passenger ID
 * @param {string} [passengerData.firstName] - Passenger first name
 * @param {string} [passengerData.lastName] - Passenger last name
 * @param {string} [passengerData.mobileNumber] - Passenger mobile number
 * @param {string} [passengerData.email] - Passenger email
 * @param {string} [passengerData.dateOfBirth] - Passenger date of birth (YYYY-MM-DD)
 * @param {string} [passengerData.gender] - Passenger gender
 * @param {string} [passengerData.nationality] - Passenger nationality
 * @param {string} [passengerData.countryOfResidence] - Passenger country of residence
 * @returns {Promise<Object>} Promise resolving to API response
 */
export const updateIndividualPassengerData = async (passengerData) => {
  try {
    console.log('=== DEBUG: updateIndividualPassengerData received ===');
    console.log('passengerData:', passengerData);
    console.log('passengerData.gender:', passengerData.gender);
    console.log('passengerData.dateOfBirth:', passengerData.dateOfBirth);

    const requestBody = {
      type: "INDIVIDUAL_PASSENGER",
      operationType: "UPDATE",
      body: {
        passengerId: passengerData.passengerId,
        firstName: passengerData.firstName || "",
        lastName: passengerData.lastName || "",
        mobileNumber: passengerData.mobileNumber || "",
        email: passengerData.email || "",
        dateOfBirth: passengerData.dateOfBirth || "",
        gender: passengerData.gender || "",
        nationality: passengerData.nationality || "",
        countryOfResidence: passengerData.countryOfResidence || ""
      }
    };

    console.log('=== DEBUG: Final API request body ===');
    console.log('requestBody:', JSON.stringify(requestBody, null, 2));

    const response = await fetch(`${API_BASE_URL}/update`, {
      method: 'PATCH',
      headers: {
        'Authorization': `Bearer ${getAuthToken()}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(requestBody)
    });

    const result = await response.json();
    console.log('Update individual passenger response:', result);

    if (!result.success) {
      throw new Error(result.message || 'Failed to update individual passenger data');
    }

    return result;
  } catch (error) {
    console.error('Error updating individual passenger data:', error);
    throw error;
  }
};

/**
 * Delete passenger data using passengerFlightId
 * @param {number} passengerFlightId - Passenger Flight ID
 * @returns {Promise<Object>} Promise resolving to API response
 */
export const deletePassengerData = async (passengerFlightId) => {
  try {
    console.log(`Deleting passenger data with passengerFlightId: ${passengerFlightId}`);

    const response = await fetch(`${API_BASE_URL}/update`, {
      method: 'PATCH',
      headers: {
        'Authorization': `Bearer ${getAuthToken()}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        type: "PASSENGER",
        operationType: "DELETE",
        body: {
          passengerFlightId: passengerFlightId
        }
      })
    });

    const result = await response.json();
    console.log('Delete passenger response:', result);

    if (!result.success) {
      throw new Error(result.message || 'Failed to delete passenger data');
    }

    return result;
  } catch (error) {
    console.error('Error deleting passenger data:', error);
    throw error;
  }
};

/**
 * Add a new passenger document
 * @param {number} passengerId - Passenger ID
 * @param {Object} documentData - Document data
 * @param {string} documentData.documentType - Type of document (e.g., 'passport', 'visa', 'aadhar card', etc.)
 * @param {string} [documentData.gender] - Gender
 * @param {string} [documentData.dateOfBirth] - Date of birth in YYYY-MM-DD format
 * @param {string} [documentData.documentNumber] - Document number
 * @param {string} [documentData.dateOfIssue] - Date of issue in YYYY-MM-DD format
 * @param {string} [documentData.dateOfExpiry] - Date of expiry in YYYY-MM-DD format
 * @param {string} [documentData.nationality] - Nationality code
 * @param {string} [documentData.placeOfIssue] - Place of issue
 * @param {string} [documentData.countryOfResidence] - Country of residence
 * @returns {Promise<Object>} Promise resolving to API response
 */
export const addPassengerDocument = async (passengerId, documentData) => {
  try {
    console.log('Adding passenger document:', { passengerId, documentData });

    const response = await fetch(`${API_BASE_URL}/update`, {
      method: 'PATCH',
      headers: {
        'Authorization': `Bearer ${getAuthToken()}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        type: "PASSENGERDOCUMENT",
        operationType: "ADD",
        body: {
          passengerId: passengerId,
          documentType: documentData.documentType,
          gender: documentData.gender || "",
          dateOfBirth: documentData.dateOfBirth || "",
          documentNumber: documentData.documentNumber || "",
          dateOfIssue: documentData.dateOfIssue || "",
          dateOfExpiry: documentData.dateOfExpiry || "",
          nationality: documentData.nationality || "",
          placeOfIssue: documentData.placeOfIssue || "",
          countryOfResidence: documentData.countryOfResidence || ""
        }
      })
    });

    const result = await response.json();
    console.log('Add passenger document response:', result);

    if (!result.success) {
      throw new Error(result.message || 'Failed to add passenger document');
    }

    return result;
  } catch (error) {
    console.error('Error adding passenger document:', error);
    throw error;
  }
};

/**
 * Update an existing passenger document
 * @param {number} documentId - Document ID
 * @param {Object} documentData - Updated document data
 * @param {string} [documentData.documentType] - Type of document
 * @param {string} [documentData.gender] - Gender
 * @param {string} [documentData.dateOfBirth] - Date of birth in YYYY-MM-DD format
 * @param {string} [documentData.documentNumber] - Document number
 * @param {string} [documentData.dateOfIssue] - Date of issue in YYYY-MM-DD format
 * @param {string} [documentData.dateOfExpiry] - Date of expiry in YYYY-MM-DD format
 * @param {string} [documentData.nationality] - Nationality code
 * @param {string} [documentData.placeOfIssue] - Place of issue
 * @param {string} [documentData.countryOfResidence] - Country of residence
 * @returns {Promise<Object>} Promise resolving to API response
 */
export const updatePassengerDocument = async (documentId, documentData) => {
  try {
    console.log('Updating passenger document:', { documentId, documentData });

    const response = await fetch(`${API_BASE_URL}/update`, {
      method: 'PATCH',
      headers: {
        'Authorization': `Bearer ${getAuthToken()}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        type: "PASSENGERDOCUMENT",
        operationType: "UPDATE",
        body: {
          documentId: documentId,
          documentType: documentData.documentType,
          gender: documentData.gender || "",
          dateOfBirth: documentData.dateOfBirth || "",
          documentNumber: documentData.documentNumber || "",
          dateOfIssue: documentData.dateOfIssue || "",
          dateOfExpiry: documentData.dateOfExpiry || "",
          nationality: documentData.nationality || "",
          placeOfIssue: documentData.placeOfIssue || "",
          countryOfResidence: documentData.countryOfResidence || ""
        }
      })
    });

    const result = await response.json();
    console.log('Update passenger document response:', result);

    if (!result.success) {
      throw new Error(result.message || 'Failed to update passenger document');
    }

    return result;
  } catch (error) {
    console.error('Error updating passenger document:', error);
    throw error;
  }
};

/**
 * Delete a passenger document
 * @param {number} documentId - Document ID
 * @returns {Promise<Object>} Promise resolving to API response
 */
export const deletePassengerDocument = async (documentId) => {
  try {
    console.log(`Deleting passenger document with ID: ${documentId}`);

    const response = await fetch(`${API_BASE_URL}/update`, {
      method: 'PATCH',
      headers: {
        'Authorization': `Bearer ${getAuthToken()}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        type: "PASSENGERDOCUMENT",
        operationType: "DELETE",
        body: {
          documentId: documentId
        }
      })
    });

    const result = await response.json();
    console.log('Delete passenger document response:', result);

    if (!result.success) {
      throw new Error(result.message || 'Failed to delete passenger document');
    }

    return result;
  } catch (error) {
    console.error('Error deleting passenger document:', error);
    throw error;
  }
};

/**
 * Fetch agents list from the API
 * @param {Object} params - Query parameters
 * @param {string} params.keyword - Search by name, email, phone
 * @param {number} params.agentId - Filter by agent ID
 * @returns {Promise<Object>} Promise resolving to agents data
 */
export const fetchAgents = async (params = {}) => {
  try {
    const queryParams = new URLSearchParams();

    if (params.keyword) {
      queryParams.append('keyword', params.keyword);
    }

    if (params.agentId) {
      queryParams.append('agentId', params.agentId);
    }

    const response = await fetch(
      business4FlyoAdminUrl(`/agents?${queryParams.toString()}`),
      {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${getBusiness4FlyoToken()}`,
          'Content-Type': 'application/json'
        }
      }
    );

    if (!response.ok) {
      if (response.status === 401) {
        console.error('Authentication error: Token may be invalid or expired');
        throw new Error('Authentication error: Please refresh or log in again');
      }
      throw new Error(`API error: ${response.status}`);
    }

    const data = await response.json();

    if (!data.success) {
      throw new Error(data.message || 'Failed to fetch agents');
    }

    return data.data || [];
  } catch (error) {
    console.error('Error fetching agents:', error);
    throw error;
  }
};

/**
 * Fetch agent details by ID
 * @param {number} agentId - Agent ID
 * @returns {Promise<Object>} Promise resolving to agent details
 */
export const fetchAgentDetails = async (agentId) => {
  try {
    const response = await fetch(
      business4FlyoAdminUrl(`/agent-info/${agentId}`),
      {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${getBusiness4FlyoToken()}`,
          'Content-Type': 'application/json'
        }
      }
    );

    if (!response.ok) {
      if (response.status === 401) {
        console.error('Authentication error: Token may be invalid or expired');
        throw new Error('Authentication error: Please refresh or log in again');
      }
      throw new Error(`API error: ${response.status}`);
    }

    const data = await response.json();

    if (!data.success) {
      throw new Error(data.message || 'Failed to fetch agent details');
    }

    return data.data;
  } catch (error) {
    console.error(`Error fetching agent details for ID ${agentId}:`, error);
    throw error;
  }
};

/**
 * Fetch browser use check-in data by user ID and ticket ID
 * @param {string} userId - User ID
 * @param {string} ticketId - Ticket ID
 * @returns {Promise<Object>} Promise resolving to browser use check-in data
 */
export const fetchBrowserUseCheckIn = async (userId, ticketId) => {
  try {
    const response = await fetch(
      adminUrl(`/browserusecheckin/${userId}/${ticketId}`),
      {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${getAuthToken()}`,
          'Content-Type': 'application/json'
        }
      }
    );

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Authentication error: Please refresh or log in again');
      }
      throw new Error(`API error: ${response.status}`);
    }

    const data = await response.json();

    if (!data.success) {
      throw new Error(data.message || 'Failed to fetch browser use check-in data');
    }

    return data;
  } catch (error) {
    console.error('Error fetching browser use check-in data:', error);
    throw error;
  }
};

/**
 * Fetch external partner check-in details by subscription ID
 * @param {string} subscriptionId - The subscription ID
 * @returns {Promise<Object>} Promise resolving to check-in details
 */
export const fetchExtCheckInBySubscriptionId = async (subscriptionId) => {
  try {
    const response = await fetch(
      adminUrl(`/getCheckInBySubscriptionID?subscriptionId=${subscriptionId}`),
      {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${getAuthToken()}`,
          'Content-Type': 'application/json'
        }
      }
    );

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Authentication error: Please refresh or log in again');
      }
      throw new Error(`API error: ${response.status}`);
    }

    const data = await response.json();

    if (!data.success) {
      throw new Error(data.message || 'Failed to fetch check-in details');
    }

    return mapExtCheckInDetail(data.data, subscriptionId);
  } catch (error) {
    console.error('Error fetching external check-in details:', error);
    throw error;
  }
};

/**
 * Upload boarding pass document for external partner check-in
 * @param {string} subscriptionId - The subscription ID
 * @param {File} file - The PDF file to upload
 * @returns {Promise<Object>} Promise resolving to upload response
 */
export const uploadExtCheckInBoardingPass = async (subscriptionId, file) => {
  try {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('type', 'EXT_PARTNER');

    const response = await fetch(
      adminUrl(`/uploadTicketDocuments?subscriptionId=${subscriptionId}`),
      {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${getAuthToken()}`
        },
        body: formData
      }
    );

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Authentication error: Please refresh or log in again');
      }
      throw new Error(`API error: ${response.status}`);
    }

    const data = await response.json();

    if (!data.success) {
      throw new Error(data.message || 'Failed to upload boarding pass');
    }

    return data;
  } catch (error) {
    console.error('Error uploading boarding pass:', error);
    throw error;
  }
};

/**
 * Update external partner PNR
 * @param {string} subscriptionId - The subscription ID
 * @param {string} pnr - The new PNR value
 * @returns {Promise<Object>} Promise resolving to update response
 */
export const updateExtCheckInPnr = async (subscriptionId, pnr) => {
  try {
    const response = await fetch(
      adminUrl('/update'),
      {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${getAuthToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          type: 'EXT_PNR',
          operationType: 'UPDATE',
          body: {
            subscriptionId,
            pnr
          }
        })
      }
    );

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Authentication error: Please refresh or log in again');
      }
      throw new Error(`API error: ${response.status}`);
    }

    const data = await response.json();

    if (!data.success) {
      throw new Error(data.message || 'Failed to update PNR');
    }

    return data;
  } catch (error) {
    console.error('Error updating PNR:', error);
    throw error;
  }
};

/**
 * Update external partner check-in status
 * @param {string} subscriptionId - The subscription ID
 * @param {string} checkinStatus - The new check-in status
 * @param {string} checkinSubStatus - Optional check-in sub status
 * @param {string} statusMessage - Optional check-in status message
 * @returns {Promise<Object>} Promise resolving to update response
 */
export const updateExtCheckInStatus = async (
  subscriptionId,
  checkinStatus,
  checkinSubStatus = '',
  statusMessage = ''
) => {
  try {
    const requestBody = {
      type: 'EXT_CHECKIN_STATUS',
      operationType: 'UPDATE',
      body: {
        subscriptionId,
        checkinStatus,
        checkinSubStatus: checkinSubStatus || '',
        statusMessage: statusMessage || ''
      }
    };

    console.log('Updating check-in status with:', requestBody);

    const response = await fetch(
      adminUrl('/update'),
      {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${getAuthToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      }
    );

    console.log('Status update response status:', response.status);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error('Status update error response:', errorData);
      if (response.status === 401) {
        throw new Error('Authentication error: Please refresh or log in again');
      }
      throw new Error(errorData.message || `API error: ${response.status}`);
    }

    const data = await response.json();
    console.log('Status update response:', data);

    if (!data.success) {
      throw new Error(data.message || 'Failed to update check-in status');
    }

    return data;
  } catch (error) {
    console.error('Error updating check-in status:', error);
    throw error;
  }
};

/**
 * Update external partner check-in sub status
 * @param {string} subscriptionId - The subscription ID
 * @param {string} checkinStatus - The check-in status
 * @param {string} checkinSubStatus - The check-in sub status
 * @param {string} statusMessage - Optional check-in status message
 * @returns {Promise<Object>} Promise resolving to update response
 */
export const updateExtCheckInSubStatus = async (
  subscriptionId,
  checkinStatus,
  checkinSubStatus,
  statusMessage = ''
) => {
  try {
    const requestBody = {
      type: 'EXT_CHECKIN_STATUS',
      operationType: 'UPDATE',
      body: {
        subscriptionId,
        checkinStatus,
        checkinSubStatus: checkinSubStatus || '',
        statusMessage: statusMessage || ''
      }
    };

    console.log('Updating check-in sub status with:', requestBody);

    const response = await fetch(
      adminUrl('/update'),
      {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${getAuthToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      }
    );

    console.log('Sub status update response status:', response.status);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error('Sub status update error response:', errorData);
      if (response.status === 401) {
        throw new Error('Authentication error: Please refresh or log in again');
      }
      throw new Error(errorData.message || `API error: ${response.status}`);
    }

    const data = await response.json();
    console.log('Sub status update response:', data);

    if (!data.success) {
      throw new Error(data.message || 'Failed to update check-in sub status');
    }

    return data;
  } catch (error) {
    console.error('Error updating check-in sub status:', error);
    throw error;
  }
};

/**
 * Update external partner passengers
 * @param {string} subscriptionId - The subscription ID
 * @param {Array} passengers - The updated passengers array
 * @returns {Promise<Object>} Promise resolving to update response
 */
export const updateExtCheckInPassengers = async (subscriptionId, passengers) => {
  try {
    const response = await fetch(
      adminUrl('/update'),
      {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${getAuthToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          type: 'EXT_PASSENGERS',
          operationType: 'UPDATE',
          body: {
            subscriptionId,
            passengers
          }
        })
      }
    );

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Authentication error: Please refresh or log in again');
      }
      throw new Error(`API error: ${response.status}`);
    }

    const data = await response.json();

    if (!data.success) {
      throw new Error(data.message || 'Failed to update passengers');
    }

    return data;
  } catch (error) {
    console.error('Error updating passengers:', error);
    throw error;
  }
};
